import { HtmlHTMLAttributes, useCallback, useEffect, useState } from 'react';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import { v4 as uuid } from 'uuid';
import { IFileViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { Delete, Upload } from '../../controller/FileController';
import { FileUsageType } from '../../types/enums';
import ProgressBar from '../ProgressBar';
import { Container } from './styles';
import { useAlert } from '../../hooks/useAlert';
import ProfilePicturePreview from './ProfilePicturePreview/profilePicturePreview';
import DocumentPreview from './DocumentPreview/documentPreview';
import UploadButton from './UploadButton/uploadButton';
import UploadArea from './UploadArea/uploadArea';

export interface CustomFile {
  id?: string;
  preview: string;
  file: File;
}
type Uploader = (
  file: File,
  onUploadProgress?: ((progress: number) => void) | undefined,
  fileUsageType?: FileUsageType,
) => [Promise<string>, () => void];

type UploaderWithFileId = (
  file: File,
  onUploadProgress?: ((progress: number) => void) | undefined,
  fileId?: string,
) => [Promise<string>, () => void];

interface DropZoneProps extends HtmlHTMLAttributes<HTMLDivElement> {
  onFile?: (file: CustomFile) => void;
  onRemoveFile?: () => void;
  onFiles?: (files: CustomFile[]) => void;
  onUpload?: (file: IFileViewModel) => void;
  uploadHandler?: UploaderWithFileId | Uploader;
  fileId?: string;
  multipleFiles: boolean;
  type: 'picture' | 'attachments';
  renderSummaryType?: 'document';
  showButtonForUpload?: boolean;
  defaultValues?: File | File[];
  renderType?: 'drop' | 'button';
  renderSummary?: boolean;
  iconButtonRender?: JSX.Element;
  widthPictureSummury?: string;
  heightPictureSummury?: string;
  isUploadOnDrop?: boolean;
}

export function DropZone({
  onFile,
  onFiles,
  onRemoveFile,
  onUpload,
  uploadHandler,
  fileId,
  multipleFiles,
  type,
  renderSummaryType,
  defaultValues,
  renderType,
  renderSummary,
  iconButtonRender,
  widthPictureSummury,
  heightPictureSummury,
  isUploadOnDrop,
  showButtonForUpload,
  ...props
}: DropZoneProps) {
  const theme = useTheme();
  const alert = useAlert();
  const [hasImg, setHasImg] = useState(false);
  const [file, setFile] = useState<CustomFile>();
  const [, setFiles] = useState<CustomFile[]>();
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadFail, setUploadFail] = useState(false);
  const [cancel, setCancel] = useState<() => void>();
  const acceptedFileTypes =
    type === 'picture'
      ? ['.png', '.jpeg', '.jpg']
      : ['.pdf', '.png', '.jpeg', '.jpg', '.docx', '.xlsx'];

  const handleDeleteDocument = () => {
    if (file?.id || fileId) {
      Delete(file?.id ? file?.id : fileId!);
    }
    setFile(undefined);
    setHasImg(false);
    onRemoveFile?.();
  };

  const handleSend = async (fileToSend: CustomFile) => {
    if (!isUploadOnDrop || sending || !fileToSend?.file) return;

    setSending(true);
    const onProgress = (p: number) => setProgress(p);

    const handlerTuple = fileId
      ? (uploadHandler as UploaderWithFileId)(fileToSend.file, onProgress, fileId)
      : (uploadHandler as Uploader)?.(fileToSend.file, onProgress) ??
        Upload(fileToSend.file, onProgress);

    const [uploadPromise, cancelUpload] = handlerTuple;
    setCancel(cancelUpload);
    uploadPromise
      .then((id) => {
        const newFile: IFileViewModel = { id, name: fileToSend?.file.name, file: fileToSend?.file };
        onUpload?.(newFile);
        setFile({ ...fileToSend, id });
      })
      .catch((error) => {
        if (error?.message !== 'canceled') {
          setUploadFail(true);
        }
        setHasImg(false);
      })
      .finally(() => {
        setSending(false);
        setProgress(0);
      });
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (multipleFiles) {
      const newFiles = acceptedFiles.map(
        (f: File) => ({ file: f, preview: URL.createObjectURL(f), id: uuid() } as CustomFile),
      );
      onFiles?.(newFiles);
      setFiles((prev) => [...(prev || []), ...newFiles]);
    } else {
      const uniqueFile = acceptedFiles[0];
      const newFile: CustomFile = { file: uniqueFile, preview: URL.createObjectURL(uniqueFile) };
      onFile?.(newFile);
      setFile(newFile);
      handleSend(newFile);
      setHasImg(acceptedFiles.length > 0);
    }
  };

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      if (
        rejections.some((r: FileRejection) =>
          r.errors.some((e: FileError) => e.code === 'file-too-large'),
        )
      ) {
        alert.show({ message: 'Arquivo muito grande, selecione menor.', type: 'error' });
      }
    },
    [alert],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: multipleFiles,
    maxSize: 100 * 1024 * 1024,
    accept: {
      'image/*': acceptedFileTypes,
    },
  });

  const handleRetryUpload = () => {
    if (file) handleSend(file);
  };

  const handleCancelUpload = () => {
    cancel?.();
    setSending(false);
    setProgress(0);
    setUploadFail(false);
  };

  useEffect(() => {
    Array.isArray(defaultValues)
      ? setFiles(
          defaultValues.map(
            (dv) => ({ file: dv, preview: URL.createObjectURL(dv), id: uuid() } as CustomFile),
          ),
        )
      : setFile({ preview: defaultValues ? URL.createObjectURL(defaultValues) : '' } as CustomFile);
    setHasImg(!!defaultValues);
  }, [defaultValues]);

  return (
    <Container
      heightPictureSummury={heightPictureSummury}
      widthPictureSummury={widthPictureSummury}
      {...props}>
      {sending || progress > 0 ? (
        <ProgressBar
          id="progress"
          label={progress ? `Enviando (${progress}%)` : 'Preparando...'}
          errorLabel="Falha no envio"
          value={progress}
          failed={uploadFail}
          onRetry={handleRetryUpload}
          onCancel={handleCancelUpload}
        />
      ) : (
        <div>
          {!hasImg || showButtonForUpload ? (
            renderType === 'button' ? (
              <UploadButton
                getRootProps={getRootProps}
                iconButtonRender={iconButtonRender}
                theme={theme}
              />
            ) : (
              <UploadArea
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                multipleFiles={multipleFiles}
                hasImg={hasImg}
              />
            )
          ) : (
            renderSummary &&
            (renderSummaryType === 'document' ? (
              <DocumentPreview file={file} handleDeleteDocument={handleDeleteDocument} />
            ) : (
              <ProfilePicturePreview
                file={file}
                getRootProps={getRootProps}
                onRemoveFile={onRemoveFile}
              />
            ))
          )}
        </div>
      )}
    </Container>
  );
}
